// =====================================================
// Apple Design Enhancement - FileCardEnhanced Component
// An enhanced file card with Apple-inspired design patterns
// including design tokens, hover elevation, and click feedback
// =====================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { KnowledgeFile } from '../../types';
import { getFileIcon, formatFileSize } from '../../utils/file-types';
import { getPublicUrl } from '../../config/storage';
import { MoreVertical, Star, Lock, Eye, Globe } from 'lucide-react';
import { useFileStore } from '../../stores/file-store';
import { useCategoryStore } from '../../stores/category-store';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { cn } from '../../utils/cn';
import { supabase } from '../../config/supabase';
import { useI18n } from '../../i18n';
import MoreMenu from '../shared/MoreMenu';
import { PERMISSION_LABELS, PERMISSION_COLORS } from '../../utils/permissions';
import FilePermissionModal from './FilePermissionModal';
import { useProgressiveAnimation } from '../../hooks/useProgressiveAnimation';
import Skeleton from '../shared/Skeleton';

dayjs.extend(relativeTime);

// =====================================================
// Types
// =====================================================

/**
 * Props for the FileCardEnhanced component
 * 
 * Requirements:
 * - 6.1: THE FileCard SHALL use Design_System tokens for colors, spacing, and typography
 * - 6.2: WHEN the user hovers over a FileCard, THE FileCard SHALL display a subtle elevation change with smooth transition
 * - 6.6: WHEN the user clicks the FileCard, THE FileCard SHALL provide tactile feedback through a brief scale animation
 */
export interface FileCardEnhancedProps {
  /** The file to display */
  file: KnowledgeFile;
  /** Index of this card in the list (for progressive animation) */
  index?: number;
  /** Total number of items in the list (for progressive animation) */
  totalItems?: number;
  /** Whether the card is in selectable mode */
  selectable?: boolean;
  /** Whether the card is currently selected */
  selected?: boolean;
  /** Callback when selection state changes */
  onSelectChange?: (id: string, checked: boolean) => void;
  /** Callback to request moving the file */
  onRequestMove?: (id: string) => void;
  /** Callback to request deleting the file */
  onRequestDelete?: (id: string) => void;
  /** Whether to animate the card entrance */
  animate?: boolean;
}

// =====================================================
// Component
// =====================================================

/**
 * FileCardEnhanced - An enhanced file card component with Apple-inspired design
 * 
 * Features:
 * - Uses design tokens for consistent styling
 * - Hover elevation effect with smooth transition
 * - Click scale animation for tactile feedback
 * - Progressive animation support for list rendering
 * - Skeleton loading for thumbnails (using Skeleton component)
 * - Graceful fallback to file type icon on thumbnail load error
 * 
 * Requirements:
 * - 6.1: THE FileCard SHALL use Design_System tokens for colors, spacing, and typography
 * - 6.2: WHEN the user hovers over a FileCard, THE FileCard SHALL display a subtle elevation change with smooth transition
 * - 6.3: THE FileCard SHALL display a skeleton placeholder while the thumbnail loads
 * - 6.4: WHEN the thumbnail fails to load, THE FileCard SHALL gracefully fall back to a file type icon
 * - 6.6: WHEN the user clicks the FileCard, THE FileCard SHALL provide tactile feedback through a brief scale animation
 */
const FileCardEnhanced: React.FC<FileCardEnhancedProps> = ({
  file,
  index = 0,
  totalItems = 1,
  selectable = false,
  selected = false,
  onSelectChange,
  onRequestMove,
  onRequestDelete,
  animate = false,
}) => {
  const navigate = useNavigate();
  // 只订阅需要的 store 方法，避免订阅整个 store 导致不必要的重渲染
  const starFile = useFileStore(state => state.starFile);
  const clearThumbnailUpdated = useFileStore(state => state.clearThumbnailUpdated);
  const fetchCategories = useCategoryStore(state => state.fetchCategories);
  const { t } = useI18n();
  
  // State for thumbnail loading
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // State for click animation
  const [isPressed, setIsPressed] = useState(false);
  
  // Menu state
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [menuTriggerRect, setMenuTriggerRect] = useState<DOMRect | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);
  
  // Get file icon
  const FileIcon = getFileIcon(file.type);
  
  // Track thumbnail key for change detection
  const lastThumbnailKeyRef = useRef<string | null>(file.thumbnail_key || null);
  // 标记是否已经加载过缩略图
  const thumbnailLoadedRef = useRef(false);
  
  // Progressive animation hook
  const { style: animationStyle, animationsEnabled } = useProgressiveAnimation(
    index,
    totalItems,
    undefined,
    'enter'
  );
  
  // =====================================================
  // Effects
  // =====================================================
  
  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [menuOpen]);
  
  // Handle thumbnail updates from store - 只在组件挂载时检查一次
  useEffect(() => {
    const isUpdated = useFileStore.getState().thumbnailUpdatedFiles.has(file.id);
    if (isUpdated) {
      // Reset state for reload
      setThumbnailUrl(null);
      setImageLoaded(false);
      setImageError(false);
      thumbnailLoadedRef.current = false;
      
      // Fetch latest thumbnail info
      const fetchLatestFileInfo = async () => {
        try {
          const { data: latestFile, error: fetchError } = await supabase
            .from('files')
            .select('thumbnail_key, thumbnail_generated_at')
            .eq('id', file.id)
            .single();

          if (fetchError || !latestFile?.thumbnail_key) return;
          
          const url = await getPublicUrl(latestFile.thumbnail_key);
          
          // Preload image
          const img = new Image();
          img.onload = () => {
            setThumbnailUrl(url);
            setImageLoaded(true);
            thumbnailLoadedRef.current = true;
          };
          img.onerror = () => {
            setImageError(true);
          };
          img.src = url;
        } catch (error) {
          console.error('Failed to refresh thumbnail:', error);
        }
      };
      
      fetchLatestFileInfo();
      clearThumbnailUpdated(file.id);
    }
  }, [file.id, file.thumbnail_key, clearThumbnailUpdated]);
  
  // 单一的缩略图加载 effect - 合并所有缩略图加载逻辑
  useEffect(() => {
    // 如果缩略图 key 没有变化且已经加载过，跳过
    const currentKey = file.thumbnail_key || null;
    if (currentKey === lastThumbnailKeyRef.current && thumbnailLoadedRef.current) {
      return;
    }
    
    // 更新 ref
    lastThumbnailKeyRef.current = currentKey;
    
    // 如果有缩略图 key，直接加载
    if (currentKey) {
      let cancelled = false;
      
      getPublicUrl(currentKey).then(url => {
        if (cancelled) return;
        
        // 预加载图片
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          setThumbnailUrl(url);
          setImageLoaded(true);
          thumbnailLoadedRef.current = true;
        };
        img.onerror = () => {
          if (cancelled) return;
          setThumbnailUrl(null);
          setImageError(true);
        };
        img.src = url;
      });
      
      return () => { cancelled = true; };
    }
    
    // 没有缩略图 key，尝试加载原始文件作为预览
    if ((file.type === 'image' || file.type === 'video' || file.type === 'pdf') && file.storage_key) {
      let cancelled = false;
      
      getPublicUrl(file.storage_key).then(url => {
        if (cancelled) return;
        setThumbnailUrl(url);
        thumbnailLoadedRef.current = true;
      }).catch(() => {
        if (cancelled) return;
        setImageError(true);
      });
      
      return () => { cancelled = true; };
    }
  }, [file.id, file.thumbnail_key, file.type, file.storage_key]);
  
  // =====================================================
  // Handlers
  // =====================================================
  
  const handleReloadImage = useCallback(() => {
    setImageLoaded(false);
    setImageError(false);
    thumbnailLoadedRef.current = false;
    setThumbnailUrl(null);
  }, []);
  
  const handleCardClick = () => {
    if (selectable) return;
    navigate(`/uadmin/files/${file.id}`);
  };
  
  const handleMouseDown = () => {
    setIsPressed(true);
  };
  
  const handleMouseUp = () => {
    setIsPressed(false);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Allow keyboard activation with Enter or Space (Requirement 8.5)
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!selectable) {
        navigate(`/uadmin/files/${file.id}`);
      }
    }
  };
  
  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    starFile(file.id, !file.starred);
    (async () => {
      await Promise.all([
        useFileStore.getState().fetchFiles(),
        fetchCategories()
      ]);
    })().catch((err: unknown) => {
      console.error('Error starring file:', err);
    });
  };
  
  const handleMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // 获取按钮位置用于定位菜单
    if (moreButtonRef.current) {
      setMenuTriggerRect(moreButtonRef.current.getBoundingClientRect());
    }
    setMenuOpen((v) => !v);
  };
  
  // =====================================================
  // Render Preview
  // =====================================================
  
  const renderPreview = () => {
    // Determine if this file type should have a thumbnail
    const hasThumbnailSupport = file.type === 'image' || file.type === 'video' || file.type === 'pdf' || file.thumbnail_key;
    
    // Show skeleton while loading (Requirement 6.3)
    // THE FileCard SHALL display a skeleton placeholder while the thumbnail loads
    if (!imageLoaded && !imageError && hasThumbnailSupport) {
      return (
        <div 
          className="absolute inset-0 flex items-center justify-center" 
          style={{ backgroundColor: 'var(--color-background-secondary)' }}
          data-testid="thumbnail-skeleton"
        >
          {/* Skeleton shimmer effect using the Skeleton component */}
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation="shimmer"
            className="absolute inset-0"
          />
          {/* Hidden image for preloading */}
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt={file.name}
              className="max-w-full max-h-full object-contain opacity-0 absolute"
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageError(true);
                handleReloadImage();
              }}
            />
          )}
        </div>
      );
    }
    
    // Show loaded thumbnail with fade-in transition
    if (thumbnailUrl && imageLoaded) {
      return (
        <div 
          className="absolute inset-0 flex items-center justify-center overflow-hidden" 
          style={{ backgroundColor: 'var(--color-background-secondary)' }}
          data-testid="thumbnail-loaded"
        >
          <img
            src={thumbnailUrl}
            alt={file.name}
            className={cn(
              "max-w-full max-h-full object-contain transition-opacity",
              "duration-[var(--duration-normal)]"
            )}
            style={{ opacity: imageLoaded ? 1 : 0 }}
          />
        </div>
      );
    }
    
    // Fallback to file type icon (Requirement 6.4)
    // WHEN the thumbnail fails to load, THE FileCard SHALL gracefully fall back to a file type icon
    return (
      <div 
        className="absolute inset-0 flex items-center justify-center"
        style={{ backgroundColor: 'var(--color-background-secondary)' }}
        data-testid="thumbnail-fallback"
      >
        <div
          className={cn(
            "flex items-center justify-center w-16 h-16 rounded-lg",
            (file.type === 'document') && 'bg-blue-50',
            (file.type as string) === 'pdf' && 'bg-orange-50',
            (file.type === 'spreadsheet') && 'bg-emerald-50',
            (file.type === 'presentation') && 'bg-purple-50',
            (file.type as string) === 'video' && 'bg-red-50',
            (file.type === 'audio') && 'bg-yellow-50',
            (file.type === 'code') && 'bg-gray-100',
            (file.type === 'image') && 'bg-teal-50',
            (file.type === 'other') && 'bg-gray-50'
          )}
          style={{ borderRadius: 'var(--radius-lg)' }}
        >
          <FileIcon 
            size={32} 
            className={cn(
              (file.type === 'document') && 'text-blue-500',
              (file.type as string) === 'pdf' && 'text-orange-500',
              (file.type === 'spreadsheet') && 'text-emerald-500',
              (file.type === 'presentation') && 'text-purple-500',
              (file.type as string) === 'video' && 'text-red-500',
              (file.type === 'audio') && 'text-yellow-500',
              (file.type === 'code') && 'text-gray-700',
              (file.type === 'image') && 'text-teal-500',
              (file.type === 'other') && 'text-gray-500'
            )}
          />
        </div>
      </div>
    );
  };
  
  // =====================================================
  // Animation Styles
  // =====================================================
  
  const getAnimationStyles = (): React.CSSProperties => {
    if (!animate || !animationsEnabled) {
      return {};
    }
    
    return {
      animationName: 'fadeSlideIn',
      animationDelay: animationStyle.animationDelay,
      animationDuration: animationStyle.animationDuration,
      animationTimingFunction: 'var(--easing-ease-out)',
      animationFillMode: 'both',
    };
  };
  
  // =====================================================
  // Render
  // =====================================================
  
  return (
    // Container wrapper for container queries (Requirement 7.2)
    // The file-card-container class enables container-type: inline-size
    <div className="file-card-container">
      <div
        onClick={handleCardClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onKeyDown={handleKeyDown}
        onMouseEnter={(e) => {
          if (!isPressed) {
            e.currentTarget.style.boxShadow = 'var(--shadow-hover)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
          setIsPressed(false);
        }}
        className={cn(
          // Container query class for responsive layout (Requirement 7.2)
          "file-card-enhanced",
          "relative flex flex-col overflow-hidden cursor-pointer group",
          // Base styles using design tokens
          "transition-all",
          // Focus indicator using keyColor token (Requirement 8.5)
          "focus-ring",
          // Selection state
          selected && "ring-2 ring-[var(--color-key)] border-[var(--color-key)]"
        )}
        tabIndex={0}
        role="button"
        aria-label={`View file: ${file.name}`}
        style={{
          // Design token-based styling (Requirement 6.1)
          backgroundColor: 'var(--color-background-primary)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--color-system-tertiary)',
          // Hover elevation effect (Requirement 6.2)
          boxShadow: 'var(--shadow-sm)',
          // Click scale animation (Requirement 6.6)
          transform: isPressed ? 'scale(0.98)' : 'scale(1)',
          // Smooth transitions
          transitionProperty: 'box-shadow, transform',
          transitionDuration: 'var(--duration-fast)',
          transitionTimingFunction: 'var(--easing-default)',
          // Progressive animation styles
          ...getAnimationStyles(),
        }}
      >
      {/* Selection checkbox */}
      {selectable && (
        <div className="absolute top-2 left-2 z-20">
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => {
              e.stopPropagation();
              onSelectChange?.(file.id, e.target.checked);
            }}
            className="h-4 w-4 accent-[var(--color-key)] cursor-pointer touch-target focus-ring"
            onClick={(e) => e.stopPropagation()}
            style={{ minWidth: 'var(--touch-target-min)', minHeight: 'var(--touch-target-min)' }}
          />
        </div>
      )}
      
      {/* Thumbnail area */}
      <div 
        className="relative aspect-video overflow-hidden"
        style={{ backgroundColor: 'var(--color-background-secondary)' }}
      >
        {renderPreview()}
        
        {/* Selection overlay */}
        {selected && (
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{ backgroundColor: 'rgba(0, 113, 227, 0.1)' }}
          />
        )}
        
        {/* Action buttons - hidden at narrow container widths via container queries */}
        <div
          ref={menuRef}
          className="file-card-actions absolute top-2 right-2 flex space-x-2 transition-opacity md:opacity-0 md:group-hover:opacity-100 opacity-100"
          style={{ transitionDuration: 'var(--duration-fast)' }}
        >
          {/* Star button */}
          <button
            onClick={handleStarClick}
            title={file.starred ? t('removeFromStarred') : t('addToStarred')}
            className={cn(
              'flex items-center justify-center rounded-full transition-colors focus-ring',
              file.starred
                ? 'text-yellow-600 bg-yellow-50'
                : 'bg-white/80 hover:text-yellow-600'
            )}
            style={{
              padding: 'var(--spacing-sm)',
              minWidth: 'var(--touch-target-min)',
              minHeight: 'var(--touch-target-min)',
              color: file.starred ? undefined : 'var(--color-system-secondary)',
              borderRadius: 'var(--radius-full)',
              transitionDuration: 'var(--duration-fast)',
            }}
          >
            <Star size={18} fill={file.starred ? 'currentColor' : 'none'} />
          </button>

          {/* More menu button */}
          <button
            ref={moreButtonRef}
            onClick={handleMoreClick}
            title={t('more')}
            className="flex items-center justify-center rounded-full transition-colors bg-white/80 focus-ring"
            style={{
              padding: 'var(--spacing-sm)',
              minWidth: 'var(--touch-target-min)',
              minHeight: 'var(--touch-target-min)',
              color: 'var(--color-system-secondary)',
              borderRadius: 'var(--radius-full)',
              transitionDuration: 'var(--duration-fast)',
            }}
          >
            <MoreVertical size={18} />
          </button>
          
          {/* Dropdown menu */}
          {menuOpen && menuTriggerRect && (
            <div onClick={(e) => e.stopPropagation()}>
              <MoreMenu
                triggerRect={menuTriggerRect}
                onRename={undefined}
                onMove={() => onRequestMove?.(file.id)}
                onDownload={undefined}
                onDelete={() => onRequestDelete?.(file.id)}
                onPermissions={() => {
                  setMenuOpen(false);
                  setShowPermissionModal(true);
                }}
                onClose={() => {
                  setMenuOpen(false);
                  setMenuTriggerRect(null);
                }}
                labels={{
                  rename: t('renameFile'),
                  moveTo: t('moveTo'),
                  download: t('downloadFile'),
                  delete: t('delete'),
                  permissions: '权限设置',
                }}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* File info section */}
      <div 
        className="flex flex-col flex-1"
        style={{ padding: 'var(--spacing-md)' }}
      >
        {/* File name */}
        <h3 
          className="mb-1 truncate"
          title={file.name}
          style={{
            font: 'var(--font-headline)',
            color: 'var(--color-system-primary)',
          }}
        >
          {file.name}
        </h3>
        
        <div className="mt-auto">
          {/* Permission indicator */}
          <div className="flex items-center gap-2 mb-2">
            {file.owner_only ? (
              <div 
                className="flex items-center" 
                title="私密，仅创建者可见"
                style={{ color: 'var(--color-info)' }}
              >
                <Lock size={12} className="mr-1" />
                <span style={{ font: 'var(--font-caption)' }}>私密</span>
              </div>
            ) : file.is_public ? (
              <div 
                className="flex items-center" 
                title="公开文件"
                style={{ color: 'var(--color-success)' }}
              >
                <Globe size={12} className="mr-1" />
                <span style={{ font: 'var(--font-caption)' }}>公开</span>
              </div>
            ) : file.min_permission_level ? (
              <div className="flex items-center" title={`需要${PERMISSION_LABELS[file.min_permission_level]}权限`}>
                <Lock size={12} className="mr-1" style={{ color: 'var(--color-system-tertiary)' }} />
                <span 
                  className={cn("px-1.5 py-0.5 rounded", PERMISSION_COLORS[file.min_permission_level])}
                  style={{ font: 'var(--font-caption)', borderRadius: 'var(--radius-sm)' }}
                >
                  {PERMISSION_LABELS[file.min_permission_level]}
                </span>
              </div>
            ) : (
              <div 
                className="flex items-center" 
                title="基础权限"
                style={{ color: 'var(--color-system-secondary)' }}
              >
                <Eye size={12} className="mr-1" />
                <span style={{ font: 'var(--font-caption)' }}>基础</span>
              </div>
            )}
          </div>
          
          {/* Tags - hidden at narrow container widths via container queries */}
          <div className="file-card-tags flex flex-wrap gap-1 mb-2">
            {(file.tags || []).slice(0, 2).map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-0.5"
                style={{
                  font: 'var(--font-caption)',
                  backgroundColor: `${tag.color}20`,
                  color: tag.color,
                  borderRadius: 'var(--radius-full)',
                }}
              >
                {tag.name}
              </span>
            ))}
            {file.tags && file.tags.length > 2 && (
              <span 
                className="inline-flex items-center px-2 py-0.5"
                style={{
                  font: 'var(--font-caption)',
                  backgroundColor: 'var(--color-background-tertiary)',
                  color: 'var(--color-system-secondary)',
                  borderRadius: 'var(--radius-full)',
                }}
              >
                +{file.tags.length - 2}
              </span>
            )}
          </div>
          
          {/* File type and size */}
          <div 
            className="flex items-center justify-between"
            style={{
              font: 'var(--font-footnote)',
              color: 'var(--color-system-secondary)',
            }}
          >
            <span className="truncate capitalize">{file.type}</span>
            <span>{formatFileSize(file.size)}</span>
          </div>
        </div>
      </div>
      
      {/* Permission modal */}
      {showPermissionModal && (
        <FilePermissionModal
          file={file}
          onClose={() => setShowPermissionModal(false)}
          onUpdate={() => {
            useFileStore.getState().fetchFiles();
          }}
        />
      )}
      </div>
    </div>
  );
};

// Wrap with React.memo to prevent unnecessary re-renders
// Only re-render when file data, selection state, or animation props change
export default React.memo(FileCardEnhanced, (prevProps, nextProps) => {
  // Custom comparison for performance optimization
  return (
    prevProps.file.id === nextProps.file.id &&
    prevProps.file.name === nextProps.file.name &&
    prevProps.file.starred === nextProps.file.starred &&
    prevProps.file.thumbnail_key === nextProps.file.thumbnail_key &&
    prevProps.file.thumbnail_generated_at === nextProps.file.thumbnail_generated_at &&
    prevProps.index === nextProps.index &&
    prevProps.totalItems === nextProps.totalItems &&
    prevProps.selectable === nextProps.selectable &&
    prevProps.selected === nextProps.selected &&
    prevProps.animate === nextProps.animate
  );
});
